'use client';

import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getFraudCvvAttemptCount, validatePaymentCvv } from '@libs/api-proxy-client';
import { logSentryError, SentryAcpCreditCardsErrors as SentryErrors, useDecisions } from '@libs/common-next';

import { MAX_CVV_ATTEMPTS } from './constants';

export interface CvvValidationOutcome {
    isValid: boolean;
    remainingAttempts: number;
}

export interface CVVChallengeContextValue {
    /** The customer's failed CVV attempts. Undefined until the count loads. */
    attemptCount?: number;
    isLoading: boolean;
    /** The customer is out of CVV attempts and has to go through full card validation. */
    isLimitReached: boolean;
    /** Checks the CVV and keeps the attempt count in sync with the response. */
    validateCvv: (cardId: number, encryptedSecurityCode: string) => Promise<CvvValidationOutcome>;
    /** Loads the attempt count again, for example after full card validation. */
    refreshAttemptCount: () => Promise<void>;
}

const CVVChallengeContext = createContext<CVVChallengeContextValue | null>(null);

async function fetchAttemptCount(): Promise<number> {
    try {
        const response = await getFraudCvvAttemptCount();
        return response?.fraudCVVAttemptCount ?? 0;
    } catch (error) {
        // The validate endpoint still enforces the limit, so fall back to the CVV check.
        logSentryError(error, SentryErrors.CVVChallenge.FetchAttemptCountFailed);
        return 0;
    }
}

/** Keeps the customer's failed CVV attempts so any component below can tell whether a CVV check is still possible. */
export function CVVChallengeProvider({ children }: PropsWithChildren) {
    const [attemptCount, setAttemptCount] = useState<number>();
    const enabled = useDecisions('as_cvv_validate');

    useEffect(() => {
        if (!enabled) return;

        let isCancelled = false;

        fetchAttemptCount().then(count => {
            if (!isCancelled) {
                setAttemptCount(count);
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [enabled]);

    const refreshAttemptCount = useCallback(async () => {
        setAttemptCount(await fetchAttemptCount());
    }, []);

    const validateCvv = useCallback(async (cardId: number, encryptedSecurityCode: string) => {
        const result = await validatePaymentCvv(cardId, encryptedSecurityCode);
        const isValid = Boolean(result?.validated);
        // A failed check without a count is treated as the limit being reached.
        const count = result?.fraudCVVAttemptCount ?? (isValid ? 0 : MAX_CVV_ATTEMPTS);

        setAttemptCount(count);

        return { isValid, remainingAttempts: Math.max(MAX_CVV_ATTEMPTS - count, 0) };
    }, []);

    const value = useMemo(
        () => ({
            attemptCount,
            isLoading: enabled && attemptCount === undefined,
            isLimitReached: attemptCount !== undefined && attemptCount >= MAX_CVV_ATTEMPTS,
            validateCvv,
            refreshAttemptCount,
        }),
        [attemptCount, enabled, validateCvv, refreshAttemptCount]
    );

    return <CVVChallengeContext.Provider value={value}>{children}</CVVChallengeContext.Provider>;
}

export function useCvvChallenge(): CVVChallengeContextValue {
    const context = useContext(CVVChallengeContext);

    if (!context) {
        throw new Error('useCvvChallenge must be used within a CVVChallengeProvider');
    }

    return context;
}
